import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, Check, X, Loader2 } from 'lucide-react';
import { useDropdownPortal } from '../../hooks/useDropdownPortal';
import {
  FORM_BORDER,
  FORM_BORDER_HOVER,
  FORM_BORDER_ERROR,
  FOCUS_RING,
  FOCUS_RING_ERROR,
  FORM_RADIUS,
  FORM_SHADOW,
  DROPDOWN_ANIMATION,
  DROPDOWN_BG,
  ITEM_HOVER,
  LABEL_TEXT,
  ERROR_TEXT,
} from './tokens';

export interface ComboboxLazyOption {
  value: string;
  label: string;
  subtitle?: string;
  data?: unknown;
}

interface ComboboxLazyProps {
  value: string;
  onChange: (value: string, option?: ComboboxLazyOption) => void;
  onSearch: (searchTerm: string) => Promise<ComboboxLazyOption[]>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  emptyMessage?: string;
  className?: string;
  allowClear?: boolean;
  debounceMs?: number;
  minSearchLength?: number;
  initialOption?: ComboboxLazyOption;
}

export const ComboboxLazy: React.FC<ComboboxLazyProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Buscar...',
  label,
  required = false,
  error,
  disabled = false,
  emptyMessage = 'No se encontraron resultados',
  className = '',
  allowClear = false,
  debounceMs = 300,
  minSearchLength = 0,
  initialOption
}) => {
  const {
    isOpen,
    isMounted,
    dropdownDataState,
    searchTerm,
    setSearchTerm,
    dropdownPos,
    containerRef,
    triggerRef,
    dropdownRef,
    handleOpen,
    handleAnimationEnd,
    close,
  } = useDropdownPortal<HTMLDivElement>(disabled);

  const [options, setOptions] = useState<ComboboxLazyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ComboboxLazyOption | null>(initialOption || null);
  const debounceTimerRef = React.useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (initialOption && initialOption.value === value) {
      setSelectedOption(initialOption);
    }
  }, [initialOption, value]);

  const performSearch = useCallback(async (term: string) => {
    if (term.length < minSearchLength) {
      setOptions([]);
      return;
    }
    try {
      setLoading(true);
      const results = await onSearch(term);
      setOptions(results);
    } catch (err) {
      console.error('Error searching:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [onSearch, minSearchLength]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (isOpen) {
      debounceTimerRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, debounceMs);
    }
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchTerm, isOpen, performSearch, debounceMs]);

  useEffect(() => {
    if (isOpen && searchTerm.length === 0) {
      performSearch('');
    }
  }, [isOpen, performSearch, searchTerm.length]);

  const handleSelect = (option: ComboboxLazyOption) => {
    setSelectedOption(option);
    onChange(option.value, option);
    close();
  };

  const handleClear = () => {
    setSelectedOption(null);
    onChange('');
    setSearchTerm('');
  };

  const displayLabel = selectedOption?.label || (value ? 'Cargando...' : placeholder);

  const renderDropdownContent = () => {
    if (loading) {
      return (
        <div className="px-4 py-3 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Buscando...
        </div>
      );
    }
    if (options.length === 0) {
      return (
        <div className="px-4 py-3 text-sm text-slate-500 text-center">
          {searchTerm.length < minSearchLength
            ? `Escribe al menos ${minSearchLength} caracteres para buscar`
            : emptyMessage}
        </div>
      );
    }
    return options.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => handleSelect(option)}
        className={`w-full px-4 py-2.5 text-left ${ITEM_HOVER} transition-colors flex items-center justify-between ${
          option.value === value ? 'bg-blue-50' : ''
        }`}
      >
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-800">
            {option.label}
          </div>
          {option.subtitle && (
            <div className="text-xs text-slate-500 mt-0.5">
              {option.subtitle}
            </div>
          )}
        </div>
        {option.value === value && (
          <Check className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />
        )}
      </button>
    ));
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className={`block ${LABEL_TEXT} mb-1.5`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <div
          ref={triggerRef}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              handleOpen();
            }
          }}
          className={`w-full px-4 py-3 text-left transition flex items-center justify-between cursor-pointer ${FORM_RADIUS} ${
            error
              ? `${FORM_BORDER_ERROR} ${FOCUS_RING_ERROR}`
              : `${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING}`
          } ${disabled ? 'bg-slate-50 cursor-not-allowed' : 'bg-white active:scale-[0.98]'}`}
        >
          <span className={selectedOption || value ? 'text-slate-800' : 'text-slate-400'}>
            {displayLabel}
          </span>
          <div className="flex items-center gap-2">
            {(selectedOption || value) && !disabled && allowClear && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            )}
            <Search className="w-4 h-4 text-slate-400" />
          </div>
        </div>

        {isMounted && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            data-state={dropdownDataState}
            data-dropdown-portal="true"
            onAnimationEnd={handleAnimationEnd}
            style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
            className={`fixed z-[200] ${DROPDOWN_BG} ${FORM_RADIUS} ${FORM_SHADOW} ${DROPDOWN_ANIMATION} max-h-60 overflow-hidden dropdown-portal-protected`}
          >
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar..."
                  className={`w-full pl-9 pr-3 py-2 text-sm ${FORM_BORDER} ${FORM_RADIUS} ${FOCUS_RING}`}
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-48 overflow-y-auto">
              {renderDropdownContent()}
            </div>
          </div>,
          document.body
        )}
      </div>

      {error && <p className={ERROR_TEXT}>{error}</p>}
    </div>
  );
};
